import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { Plus, X, Sparkles, Mic, Loader2, Trash2, Users } from "lucide-react";
import { toast } from "sonner";

export default function Meetings() {
  const [meetings, setMeetings] = useState([]);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [transcriptDraft, setTranscriptDraft] = useState("");

  const refresh = async () => {
    const r = await api.get("/meetings");
    setMeetings(r.data);
    if (!active && r.data[0]) {
      setActive(r.data[0]);
      setTranscriptDraft(r.data[0].transcript || "");
    }
  };
  useEffect(() => { refresh(); }, []);

  const create = async () => {
    if (!form.title) { toast.error("Title required"); return; }
    try {
      const r = await api.post("/meetings", {
        ...form,
        attendees: (form.attendees_str || "").split(",").map((s) => s.trim()).filter(Boolean),
      });
      setActive(r.data); setTranscriptDraft(r.data.transcript || "");
      refresh(); setOpen(false); setForm({});
      toast.success("Meeting created");
    } catch (e) { toast.error("Failed"); }
  };

  const saveTranscript = async () => {
    if (!active) return;
    const r = await api.put(`/meetings/${active.id}`, { transcript: transcriptDraft });
    setActive(r.data); setEditMode(false); refresh();
    toast.success("Transcript saved");
  };

  const summarize = async () => {
    if (!active) return;
    setBusy(true);
    try {
      const r = await api.post(`/meetings/${active.id}/summarize`);
      setActive(r.data); refresh();
      toast.success("AI summary complete");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Summarize failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete meeting?")) return;
    await api.delete(`/meetings/${id}`);
    setActive(null);
    refresh();
  };

  return (
    <div className="p-8 space-y-6" data-testid="meetings-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2">// Meeting Intelligence</div>
          <h1 className="font-display text-4xl font-medium text-white tracking-tight">Meetings</h1>
          <p className="text-slate-400 mt-2 text-sm">Transcripts in, executive summaries and action items out.</p>
        </div>
        <button
          onClick={() => { setForm({}); setOpen(true); }}
          data-testid="new-meeting-btn"
          className="px-4 py-2 flex items-center gap-2 font-medium text-sm"
          style={{ background: "var(--gold-500)", color: "#050B14" }}
        >
          <Plus className="w-4 h-4" /> New Meeting
        </button>
      </div>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-4 space-y-2">
          <div className="label-eyebrow mb-2">// Sessions ({meetings.length})</div>
          {meetings.map((m) => (
            <motion.div
              layout key={m.id}
              onClick={() => { setActive(m); setTranscriptDraft(m.transcript || ""); setEditMode(false); }}
              data-testid={`meeting-${m.id}`}
              className={`p-4 cursor-pointer border ${active?.id === m.id ? "border-[#D4AF37]/50 bg-[#10182B]" : "border-white/10 hover:border-[#D4AF37]/30 bg-[#0A111F]"}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Mic className="w-3.5 h-3.5 text-slate-500" strokeWidth={1.5} />
                <span className="label-eyebrow">{m.summary ? "synthesized" : "raw"}</span>
              </div>
              <div className="font-display text-base text-white leading-tight">{m.title}</div>
              <div className="flex items-center gap-2 mt-2 text-[11px] text-slate-500 font-mono">
                <Users className="w-3 h-3" />
                <span>{(m.attendees || []).length} attendees</span>
              </div>
            </motion.div>
          ))}
          {!meetings.length && <div className="text-sm text-slate-500 p-6 border border-white/5">No meetings yet.</div>}
        </div>

        <div className="col-span-12 lg:col-span-8 space-y-4">
          {active ? (
            <>
              <div className="nexus-card p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="label-eyebrow mb-1">// {new Date(active.date).toLocaleDateString()}</div>
                    <h2 className="font-display text-2xl text-white">{active.title}</h2>
                    <div className="text-xs text-slate-400 mt-2">{(active.attendees || []).join(" · ")}</div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={summarize}
                      disabled={busy || !active.transcript}
                      data-testid="summarize-btn"
                      className="px-4 py-2 font-medium text-sm flex items-center gap-2 disabled:opacity-50"
                      style={{ background: "var(--gold-500)", color: "#050B14" }}
                    >
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                      Synthesize
                    </button>
                    <button onClick={() => remove(active.id)} className="p-2 border border-white/10 text-slate-500 hover:text-red-400 hover:border-red-500/50">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* AI synthesis output */}
              {(active.summary || active.action_items?.length || active.decisions?.length) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {active.summary && (
                    <div className="nexus-card p-5 md:col-span-2" data-testid="meeting-summary">
                      <div className="label-eyebrow mb-2">// Executive Summary</div>
                      <div className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">{active.summary}</div>
                    </div>
                  )}
                  {active.decisions?.length > 0 && (
                    <div className="nexus-card p-5">
                      <div className="label-eyebrow mb-3">// Key Decisions</div>
                      <ul className="space-y-2">
                        {active.decisions.map((d, i) => (
                          <li key={i} className="text-sm text-slate-300 flex gap-2">
                            <span className="text-[#D4AF37] mt-1">▸</span>
                            <span>{d}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {active.action_items?.length > 0 && (
                    <div className="nexus-card p-5">
                      <div className="label-eyebrow mb-3">// Action Items</div>
                      <ul className="space-y-2">
                        {active.action_items.map((a, i) => (
                          <li key={i} className="text-sm text-slate-300 flex gap-2">
                            <span className="text-[#D4AF37] mt-1">▸</span>
                            <span>{a}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {active.follow_up_email && (
                    <div className="nexus-card p-5 md:col-span-2">
                      <div className="flex items-center justify-between mb-2">
                        <div className="label-eyebrow">// Follow-up Draft</div>
                        <button
                          onClick={() => { navigator.clipboard.writeText(active.follow_up_email); toast.success("Copied"); }}
                          className="text-[10px] font-mono tracking-widest text-slate-400 hover:text-[#D4AF37]"
                        >COPY</button>
                      </div>
                      <div className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed font-body">{active.follow_up_email}</div>
                    </div>
                  )}
                </div>
              )}

              {/* Transcript */}
              <div className="nexus-card p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="label-eyebrow">// Transcript</div>
                  {!editMode ? (
                    <button onClick={() => setEditMode(true)} className="text-xs font-mono tracking-widest text-slate-400 hover:text-[#D4AF37]">EDIT</button>
                  ) : (
                    <div className="flex gap-2">
                      <button onClick={() => { setEditMode(false); setTranscriptDraft(active.transcript || ""); }} className="text-xs font-mono tracking-widest text-slate-500">CANCEL</button>
                      <button onClick={saveTranscript} data-testid="save-transcript-btn" className="text-xs font-mono tracking-widest text-[#D4AF37]">SAVE</button>
                    </div>
                  )}
                </div>
                {editMode ? (
                  <textarea
                    data-testid="transcript-editor"
                    rows={14}
                    value={transcriptDraft}
                    onChange={(e) => setTranscriptDraft(e.target.value)}
                    placeholder="Paste meeting transcript here…"
                    className="w-full px-3 py-2 bg-[#050B14] border border-white/10 text-white font-mono text-xs focus:border-[#D4AF37] outline-none resize-none leading-relaxed"
                  />
                ) : (
                  <div className="text-xs text-slate-400 font-mono whitespace-pre-wrap leading-relaxed max-h-[300px] overflow-y-auto">
                    {active.transcript || <span className="text-slate-600">No transcript. Edit to add one.</span>}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="nexus-card p-10 text-center text-slate-500 text-sm">Select a meeting or create one.</div>
          )}
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setOpen(false)}>
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="bg-[#0A111F] border border-white/10 p-8 w-full max-w-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="label-eyebrow mb-1">// New Session</div>
                <h2 className="font-display text-2xl text-white">Create Meeting</h2>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <Field label="Title *">
                <input data-testid="meet-title" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className="form-input-dk" />
              </Field>
              <Field label="Attendees (comma-separated)">
                <input value={form.attendees_str || ""} onChange={(e) => setForm({ ...form, attendees_str: e.target.value })} className="form-input-dk" placeholder="e.g., Adaeze Okonkwo, Dr. Ngozi Eze" />
              </Field>
              <Field label="Transcript (paste or add later)">
                <textarea rows={8} value={form.transcript || ""} onChange={(e) => setForm({ ...form, transcript: e.target.value })} className="form-input-dk resize-none" />
              </Field>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setOpen(false)} className="px-5 py-2 border border-white/10 text-slate-300">Cancel</button>
              <button onClick={create} data-testid="create-meeting-btn" className="px-5 py-2 font-medium" style={{ background: "var(--gold-500)", color: "#050B14" }}>Create</button>
            </div>
          </motion.div>
        </div>
      )}
      <style>{`.form-input-dk { width:100%; padding:0.5rem 0.75rem; background:#050B14; border:1px solid rgba(255,255,255,0.1); color:#F8FAFC; font-family:'IBM Plex Mono', monospace; font-size:0.875rem; outline:none; } .form-input-dk:focus { border-color:#D4AF37; }`}</style>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="label-eyebrow block mb-1.5">{label}</label>
    {children}
  </div>
);
